import { useCallback, useEffect, useRef, useState } from 'react';
import { getInterfaceSettings, saveInterfaceSettings } from 'src/backend/user';
import useDebounce from './useDebounce';

/**
 * Generic hook to persist MUI DataGrid settings (columns, sorting, filters, etc.)
 * per user + table key using an optimistic UI with debounced backend sync.
 *
 * It does not assume any particular shape for the settings object; callers
 * typically store:
 *   - columnVisibilityModel
 *   - columnOrder
 *   - filterModel
 *   - sortModel
 *
 * @param {Object} params
 * @param {string | undefined} params.tableKey  Unique key for this table (per route/view).
 * @param {Object} [params.defaultConfig]       Default settings to seed the state.
 *        NOTE: This hook does NOT react to changes in defaultConfig after mount.
 *        If you need dynamic defaults, memoize defaultConfig with useMemo in the
 *        calling component to avoid creating a new object reference each render.
 */
export default function usePersistentGridSettings({
  tableKey,
  defaultConfig = {},
}) {
  const initiallyLoaded = !tableKey;

  const [settings, setSettings] = useState(() => ({ ...defaultConfig }));
  const [hasLoadedFromServer, setHasLoadedFromServer] =
    useState(initiallyLoaded);
  const [dirtySettings, setDirtySettings] = useState(null);
  const hasLoadedRef = useRef(initiallyLoaded);

  // Keep defaults in sync until we have loaded from the backend at least once.
  useEffect(() => {
    if (hasLoadedFromServer) return;
    if (Object.keys(defaultConfig).length === 0) return;

    setSettings((prev) => ({
      ...prev,
      ...defaultConfig,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasLoadedFromServer]);

  useEffect(() => {
    if (!tableKey) {
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const serverValue = await getInterfaceSettings(tableKey);

        if (!cancelled && serverValue && typeof serverValue === 'object') {
          setSettings((prev) => ({
            ...prev,
            ...serverValue,
          }));
          setDirtySettings(null);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        if (!cancelled) {
          hasLoadedRef.current = true;
          setHasLoadedFromServer(true);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [tableKey]);

  const markDirty = useCallback((next) => {
    if (!hasLoadedRef.current) {
      return;
    }
    setDirtySettings(next);
  }, []);

  const updateSettings = useCallback(
    (partial) => {
      setSettings((prev) => {
        const next = {
          ...prev,
          ...partial,
        };

        const prevJson = JSON.stringify(prev);
        const nextJson = JSON.stringify(next);
        if (prevJson === nextJson) {
          return prev;
        }

        markDirty(next);
        return next;
      });
    },
    [markDirty]
  );

  const debouncedSettings = useDebounce(dirtySettings, 2000);

  useEffect(() => {
    if (!tableKey) return;
    if (!hasLoadedFromServer) return;
    if (!debouncedSettings) return;

    let cancelled = false;

    const save = async () => {
      try {
        await saveInterfaceSettings(tableKey, debouncedSettings);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        if (!cancelled) {
          setDirtySettings((prev) =>
            prev === debouncedSettings ? null : prev
          );
        }
      }
    };

    save();

    return () => {
      cancelled = true;
    };
  }, [tableKey, debouncedSettings, hasLoadedFromServer]);

  return {
    settings,
    loading: !hasLoadedFromServer,
    updateSettings,
  };
}
